import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './ui';
import s from './DatePicker.module.css';

type DatePickerProps = {
  /** ISO `YYYY-MM-DD`, or '' for empty. */
  value: string;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Shows a "Clear" action in the footer — for optional dates. */
  clearable?: boolean;
  /** Inclusive bounds, also `YYYY-MM-DD`. */
  min?: string;
  max?: string;
  ariaLabel?: string;
  className?: string;
};

const POPUP_MARGIN = 6;
const POPUP_WIDTH = 268;
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/*
 * Dates here are plain calendar days, never instants, so everything is done on
 * the `YYYY-MM-DD` string and on UTC-normalised Dates. Going through local
 * time would shift the day by one either side of midnight for anyone east or
 * west of UTC — the classic "my transaction landed on the wrong date" bug.
 */
function parse(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(`${iso}T00:00:00.000Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function format(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  // Clamp to the last day when the target month is shorter — stepping from
  // the 31st must land on the 30th, not spill into the following month.
  const day = next.getUTCDate();
  next.setUTCDate(1);
  next.setUTCMonth(next.getUTCMonth() + months);
  const lastDay = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate();
  next.setUTCDate(Math.min(day, lastDay));
  return next;
}

function todayUtc(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

/** Human label for the trigger: "12 Aug 2026". */
function label(date: Date): string {
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]!.slice(0, 3)} ${date.getUTCFullYear()}`;
}

/**
 * A calendar styled to the app's own design.
 *
 * `<input type="date">` can't be themed: the picker is drawn by the browser,
 * so it arrives as a light-mode popup in a dark interface and looks different
 * in every browser. The text field also enforces the OS locale's order, which
 * turns 03/04 into a coin flip.
 *
 * Structure follows Select: a portal at the document root with fixed
 * coordinates, so cards and modals with `overflow: hidden` can't clip it.
 */
export function DatePicker({
  value,
  onChange,
  id,
  placeholder = 'Pick a date',
  disabled,
  clearable,
  min,
  max,
  ariaLabel,
  className,
}: DatePickerProps) {
  const selected = parse(value);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState<Date>(() => selected ?? todayUtc());
  // Exactly one of `top` / `bottom` is set: opening downward pins the top edge
  // to the trigger, opening upward pins the bottom edge to it.
  const [rect, setRect] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    maxHeight: number;
  } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const minDate = min ? parse(min) : null;
  const maxDate = max ? parse(max) : null;
  const today = todayUtc();

  const outOfRange = useCallback(
    (date: Date) => Boolean((minDate && date < minDate) || (maxDate && date > maxDate)),
    [minDate, maxDate],
  );

  const position = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const box = trigger.getBoundingClientRect();
    const height = popupRef.current?.offsetHeight ?? 320;
    // Measure rather than assume: the stylesheet widens the calendar on
    // phones, so clamping against the desktop constant would let it hang off
    // the right edge on exactly the screens with no room to spare.
    const width = popupRef.current?.offsetWidth ?? POPUP_WIDTH;
    const below = window.innerHeight - box.bottom - POPUP_MARGIN;
    // Flip above when there isn't room beneath — and never let either edge
    // run off screen on a narrow phone.
    const openUp = below < height && box.top > below;

    // The measured height only decides which way to flip. Placement pins an
    // edge to the trigger instead of deriving a coordinate from that height —
    // on the first open there is no popup to measure yet, and the 320 estimate
    // would have left the calendar hanging clear of the field it belongs to.
    setRect({
      top: openUp ? undefined : box.bottom + POPUP_MARGIN,
      bottom: openUp ? window.innerHeight - box.top + POPUP_MARGIN : undefined,
      left: Math.max(POPUP_MARGIN, Math.min(box.left, window.innerWidth - width - POPUP_MARGIN)),
      // Keeps a tall calendar on screen in a short viewport, which the old
      // top-clamp did by detaching it from the trigger instead.
      maxHeight: Math.max(0, (openUp ? box.top - POPUP_MARGIN : below) - POPUP_MARGIN),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    position();
  }, [open, position, cursor]);

  // Move focus into the calendar so the arrow keys work without a click.
  useEffect(() => {
    if (open) popupRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const reposition = () => position();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, position]);

  // Outside click closes. Listens on `click` rather than `mousedown` so a day
  // button isn't unmounted before its own click lands.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  // Reopening should always start from what's currently selected, not from
  // wherever the user browsed to last time.
  function openCalendar() {
    if (disabled) return;
    setCursor(selected ?? todayUtc());
    setOpen(true);
  }

  function commit(date: Date) {
    if (outOfRange(date)) return;
    onChange(format(date));
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openCalendar();
    } else if (e.key === 'Backspace' && clearable && value) {
      e.preventDefault();
      onChange('');
    }
  }

  function onCalendarKeyDown(e: React.KeyboardEvent) {
    const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (e.key in moves) {
      e.preventDefault();
      setCursor((c) => addDays(c, moves[e.key]!));
    } else if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      setCursor((c) => addMonths(c, e.key === 'PageUp' ? -1 : 1));
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      setCursor((c) => {
        const d = new Date(c);
        d.setUTCDate(e.key === 'Home' ? 1 : new Date(Date.UTC(c.getUTCFullYear(), c.getUTCMonth() + 1, 0)).getUTCDate());
        return d;
      });
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      commit(cursor);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  // The grid always starts on a Monday and runs six weeks, so the popup never
  // changes height as you page through months.
  const monthStart = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1));
  const leadingBlanks = (monthStart.getUTCDay() + 6) % 7;
  const gridStart = addDays(monthStart, -leadingBlanks);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={cx(s.trigger, open && s.triggerOpen, className)}
        onClick={() => (open ? setOpen(false) : openCalendar())}
        onKeyDown={onTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={cx(s.value, !selected && s.placeholder)}>
          {selected ? label(selected) : placeholder}
        </span>
        <svg className={s.icon} width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      {open && rect
        ? createPortal(
            <div
              ref={popupRef}
              className={s.popup}
              style={{
                top: rect.top,
                bottom: rect.bottom,
                left: rect.left,
                width: POPUP_WIDTH,
                maxHeight: rect.maxHeight,
              }}
              role="dialog"
              aria-label="Choose a date"
              onKeyDown={onCalendarKeyDown}
              // Focusable so arrow keys reach the handler the moment it opens.
              tabIndex={-1}
            >
              <div className={s.head}>
                <button
                  type="button"
                  className={s.nav}
                  onClick={() => setCursor((c) => addMonths(c, -1))}
                  aria-label="Previous month"
                >
                  ‹
                </button>
                <div className={s.monthLabel}>
                  {MONTHS[cursor.getUTCMonth()]} {cursor.getUTCFullYear()}
                </div>
                <button
                  type="button"
                  className={s.nav}
                  onClick={() => setCursor((c) => addMonths(c, 1))}
                  aria-label="Next month"
                >
                  ›
                </button>
              </div>

              <div className={s.weekdays}>
                {WEEKDAYS.map((d, i) => (
                  <span key={i}>{d}</span>
                ))}
              </div>

              <div className={s.grid}>
                {days.map((day) => {
                  const iso = format(day);
                  const inMonth = day.getUTCMonth() === cursor.getUTCMonth();
                  const disabledDay = outOfRange(day);
                  return (
                    <button
                      key={iso}
                      type="button"
                      className={cx(
                        s.day,
                        !inMonth && s.dayOutside,
                        iso === value && s.daySelected,
                        iso === format(today) && iso !== value && s.dayToday,
                        iso === format(cursor) && s.dayCursor,
                      )}
                      onClick={() => commit(day)}
                      disabled={disabledDay}
                      aria-current={iso === format(today) ? 'date' : undefined}
                      aria-pressed={iso === value}
                    >
                      {day.getUTCDate()}
                    </button>
                  );
                })}
              </div>

              <div className={s.footer}>
                <button
                  type="button"
                  className={s.footerBtn}
                  onClick={() => commit(today)}
                  disabled={outOfRange(today)}
                >
                  Today
                </button>
                {clearable ? (
                  <button
                    type="button"
                    className={s.footerBtn}
                    onClick={() => {
                      onChange('');
                      setOpen(false);
                      triggerRef.current?.focus();
                    }}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
